import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import routes from 'src/Resources/routes.json';
import { TokenContext } from 'src/contexts/tokenContext';
import { installationUrl } from 'src/utils/productRoutes';
import { stashRedirect } from 'src/utils/postLoginRedirect';

/**
 * Landing point for deep links coming from the ERP: /go/SN-260051
 *
 * The ERP knows only its own docname. It does not know monitor's numeric installation id, which
 * product route prefix that installation lives under, or which tab to open — so it sends the
 * docname and monitor works the rest out here. That is what lets the destination change without
 * the ERP changing anything.
 *
 * This is a redirect, not a page: it renders a spinner, then replaces itself in history so the
 * back button returns to wherever the user came from rather than re-running the lookup.
 */

/**
 * Where an ERP link lands. Deliberately one named constant: the destination already moved once
 * (Live View -> Tickets), and the ERP side must never need a change when it moves again.
 *
 * Tickets is safe for every installation — it is in the `allowedWhenDisabled` tab list, so it
 * renders even when dataCollectionEnabled is false, unlike the Live view.
 */
const TARGET_TAB = routes.installationTickets;

type State = 'loading' | 'notFound' | 'unavailable';

function ErpLinkResolver() {
  const { erpId } = useParams<{ erpId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { removeToken } = useContext(TokenContext);
  const [state, setState] = useState<State>('loading');
  // The path exactly as the browser has it. Re-stashing this rather than rebuilding "/go/" + erpId
  // keeps the round trip byte-identical, with no chance of an encoding mismatch between the value
  // useParams decoded and the value we would send the user back to.
  const selfPath = location.pathname;

  useEffect(() => {
    if (!erpId) {
      setState('notFound');
      return;
    }

    // Reset on every docname: without this, navigating /go/A -> /go/B leaves A's error page on
    // screen for the whole of B's request.
    setState('loading');

    let cancelled = false;

    axiosConfig
      // axiosConfig sets no default timeout, so an unresponsive backend would spin forever with no
      // way out — the Retry button only exists on the error page this timeout lets us reach.
      .get('/ResolveErpLink', { params: { erpId }, timeout: 15000 })
      .then((res) => {
        if (cancelled) return;

        // Validate before navigating. A proxy interstitial or a truncated body can return 200 with
        // a payload that is not this object, and reading .id off it would either throw into the
        // catch below (reported as "not linked", which is wrong) or build a URL containing the
        // literal string "undefined" and navigate there.
        const { id, product } = res.data ?? {};
        if (typeof id !== 'number' || typeof product !== 'number') {
          setState('unavailable');
          return;
        }

        navigate(installationUrl(product, id, TARGET_TAB), { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;

        // 401 means the session died, not that the link is bad — the usual cause is a
        // "remember me" token older than the server's one-day session lifetime, which leaves the
        // app looking logged in until the first request fails. Keep the destination so the user
        // arrives here again after logging in, instead of being dumped on their product list.
        if (err.response?.status === 401) {
          stashRedirect(selfPath);
          removeToken();
          navigate(routes.login);
          return;
        }

        // A 404 already merges "no such installation" with "not yours" on the server, and
        // splitting THOSE two in the UI would leak which docnames exist to anyone who can log in.
        // That argument does not extend to transport and server errors: telling an installer on
        // flaky mobile data that their link is not shared with them sends them to support for a
        // link that is perfectly good. Those get their own, retryable message.
        setState(err.response?.status === 404 ? 'notFound' : 'unavailable');
      });

    return () => {
      cancelled = true;
    };
    // Keyed on the docname alone. `removeToken` is rebuilt on every TokenContextProvider render,
    // so including it would re-fire the lookup on unrelated context updates; `navigate` and
    // `selfPath` are both fixed for a given docname. The `cancelled` flag guards the case that
    // does re-run it — the docname changing while a request is still in flight.
    // (This project is React 17.0.2 on legacy ReactDOM.render with no StrictMode, so there is no
    // development double-invoke to worry about.)
  }, [erpId]);

  if (state === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const unavailable = state === 'unavailable';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 1,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Typography variant="h4">
        <FormattedMessage
          id={unavailable ? 'erpLinkUnavailable' : 'erpLinkNotFound'}
          defaultMessage="Installation not found"
        />
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <FormattedMessage
          id={unavailable ? 'erpLinkUnavailableHint' : 'erpLinkNotFoundHint'}
          defaultMessage="This installation is not linked to monitor yet, or it is not shared with your account."
        />
      </Typography>

      {/* This route sits OUTSIDE SidebarLayout (it normally only redirects), so the failure state
          renders with no sidebar, header or navigation at all. Without a way out the user's only
          recovery is the browser Back button. */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {unavailable && (
          <Button variant="contained" onClick={() => window.location.reload()}>
            <FormattedMessage id="retry" defaultMessage="Retry" />
          </Button>
        )}
        <Button variant="outlined" onClick={() => navigate('/')}>
          <FormattedMessage id="erpLinkGoHome" defaultMessage="Go to my installations" />
        </Button>
      </Stack>
    </Box>
  );
}

export default ErpLinkResolver;
